import { PublicClientApplication, type AccountInfo } from "@azure/msal-browser";
import { ADMIN_CONFIG } from "./config.js";

/**
 * Cache the MSAL init *promise* (not the resolved instance) so that
 * concurrent first-callers don't each construct their own PublicClientApplication.
 * Previously the cache was on the resolved instance, which left a window where
 * two callers awaiting initialize() would each create a fresh client. The
 * second client would then miss the auth callback that the first one was
 * processing.
 */
let _initPromise: Promise<PublicClientApplication> | null = null;

export function getMsal(): Promise<PublicClientApplication> {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    const app = new PublicClientApplication({
      auth: {
        clientId: ADMIN_CONFIG.clientId,
        authority: `https://login.microsoftonline.com/${ADMIN_CONFIG.tenantId}`,
        // Always the origin ROOT — the only registered SPA redirect URI.
        // Every visitor must now sign in from wherever they landed (e.g. a
        // bookmarked /index.html), and Entra hard-rejects unregistered paths
        // (AADSTS50011). MSAL returns the user to their original URL anyway
        // (navigateToLoginRequestUrl).
        redirectUri: `${window.location.origin}/`,
      },
      cache: {
        cacheLocation: "sessionStorage",
      },
    });
    await app.initialize();
    // Process any pending auth callback (popup → here with #code=...).
    // In a popup window, MSAL also broadcasts the result back to the parent.
    await app.handleRedirectPromise();
    return app;
  })();
  return _initPromise;
}

export async function getActiveAccount(): Promise<AccountInfo | null> {
  const app = await getMsal();
  const accounts = app.getAllAccounts();
  return accounts[0] ?? null;
}

/**
 * Sign in via FULL-PAGE REDIRECT, not popup.
 *
 * Why not popup: Microsoft's login.microsoftonline.com sets Cross-Origin-Opener-Policy
 * which severs window.opener when the popup navigates cross-origin. After auth the
 * popup window has no opener, MSAL can't postMessage results back to the parent,
 * the popup hangs, and any subsequent loginPopup throws block_nested_popups.
 *
 * Redirect flow has none of those failure modes — the main window navigates to
 * Microsoft, authenticates, comes back with #code=… in the URL, and main.tsx's
 * boot logic detects the callback and calls handleRedirectPromise() to complete
 * the handshake. Slight UX cost (full page reload), big robustness win.
 */
export async function signIn(): Promise<void> {
  const app = await getMsal();
  await app.loginRedirect({ scopes: [...ADMIN_CONFIG.scopes] });
  // Page navigates away; nothing after this runs.
}

export async function signOut(): Promise<void> {
  const app = await getMsal();
  const account = await getActiveAccount();
  if (!account) return;
  await app.logoutRedirect({ account });
}

/** Acquire a Graph access token. Tries silent first, falls back to redirect. */
export async function getToken(): Promise<string> {
  const app = await getMsal();
  const account = await getActiveAccount();
  if (!account) throw new Error("Not signed in");
  try {
    const res = await app.acquireTokenSilent({ scopes: [...ADMIN_CONFIG.scopes], account });
    return res.accessToken;
  } catch {
    // Silent failed — kick the user through a full re-auth. The current page
    // state is lost but admin sessions are short-lived enough that this is fine.
    await app.acquireTokenRedirect({ scopes: [...ADMIN_CONFIG.scopes], account });
    throw new Error("Re-authentication required — page will redirect");
  }
}

/** Expiry (ms) from a JWT's `exp` claim, or 0 if it can't be read. */
function idTokenExpMs(jwt: string): number {
  try {
    const part = jwt.split(".")[1]!;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const { exp } = JSON.parse(atob(padded)) as { exp?: number };
    return typeof exp === "number" ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

/**
 * The ID token (aud = our clientId, carries the `roles` claim) — sent as the
 * Bearer to the droplet's /admin API, which validates it against Entra's JWKS.
 *
 * acquireTokenSilent keeps the ACCESS token fresh but will happily return a
 * CACHED id token that has already expired (MSAL doesn't renew the id token
 * while the access token is still valid). Since the /admin API and /auth/session
 * validate the id token's `exp`, a long editing session would eventually send a
 * stale token — the "exp claim timestamp check failed" error. So: if the id
 * token is expired or within 5 min of it, force a network refresh.
 */
export async function getIdToken(): Promise<string> {
  const app = await getMsal();
  const account = await getActiveAccount();
  if (!account) throw new Error("Not signed in");
  const SKEW_MS = 5 * 60 * 1000;
  try {
    const req = { scopes: [...ADMIN_CONFIG.scopes], account };
    let res = await app.acquireTokenSilent(req);
    if (idTokenExpMs(res.idToken) < Date.now() + SKEW_MS) {
      res = await app.acquireTokenSilent({ ...req, forceRefresh: true });
    }
    return res.idToken;
  } catch {
    await app.acquireTokenRedirect({ scopes: [...ADMIN_CONFIG.scopes], account });
    throw new Error("Re-authentication required — page will redirect");
  }
}
